/*
    Do not use ContentProgress#getMsLeftInTimeLimit. It skips the extendExamTime accommodation before the
    exam starts, and canLaunchLesson depends on that.
*/
import { type StreamProgress } from 'ContentProgress';
import { type CurrentUser } from 'Users';
import { type StreamForStreamDashboard } from 'Lessons';

const HOUR_IN_MILLISECONDS = 60 * 60 * 1000;

// See Lesson.Stream.EXAM_EXTENSION_MULTIPLIER and stream_progress.rb#time_limit_hours.
const EXAM_EXTENSION_MULTIPLIER = 0.5;

const getMsUntil = (timeRunsOutAt: number) => Math.max(0, 1000 * timeRunsOutAt - Date.now());

/** Mirrors StreamProgress#msLeftInTimeLimit. */
export const getMsLeftFromStreamProgress = (progress: StreamProgress | null | undefined) =>
    progress?.timeRunsOutAt ? getMsUntil(progress.timeRunsOutAt) : null;

export const hasTimeLimit = (stream: StreamForStreamDashboard) => !!stream.timeLimitHours;

/** Mirrors Stream#timeLimitHours, including the extendExamTime accommodation. */
export const getTimeLimitHours = (stream: StreamForStreamDashboard, user: CurrentUser | null) => {
    const timeLimitHours = stream.timeLimitHours;
    if (timeLimitHours === null) return null;
    if (!user?.extendExamTime) return timeLimitHours;
    return timeLimitHours + timeLimitHours * EXAM_EXTENSION_MULTIPLIER;
};

/**
 * Mirrors Stream#msLeftInTimeLimit: the server-set deadline once the timer runs, otherwise the full allowance.
 * `timeLimitHours` must already include the extendExamTime accommodation (see getTimeLimitHours).
 */
export const getMsLeftForTimer = ({
    timeRunsOutAt,
    timeLimitHours,
}: {
    timeRunsOutAt: number | null;
    timeLimitHours: number | null;
}) => {
    if (timeRunsOutAt) return getMsUntil(timeRunsOutAt);
    if (timeLimitHours) return timeLimitHours * HOUR_IN_MILLISECONDS;
    return null;
};
